import React from 'react';
import { CloudOff, RefreshCw, Check, AlertCircle, Database } from 'lucide-react';

export type SyncStatus = 'synced' | 'syncing' | 'error' | 'offline';

interface SyncStatusIndicatorProps {
    status: SyncStatus;
    lastSyncTime: Date | null;
    sourceOfTruth?: 'backend' | 'localStorage';
}

const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({
    status,
    lastSyncTime,
    sourceOfTruth = 'backend'
}) => {
    const getStatusDisplay = () => {
        switch (status) {
            case 'synced':
                return {
                    icon: <Check size={16} />,
                    text: 'Saved',
                    color: 'text-green-600',
                    bg: 'bg-green-50 dark:bg-green-900/20',
                    border: 'border-green-200 dark:border-green-800'
                };
            case 'syncing':
                return {
                    icon: <RefreshCw size={16} className="animate-spin" />,
                    text: 'Saving...',
                    color: 'text-blue-600',
                    bg: 'bg-blue-50 dark:bg-blue-900/20',
                    border: 'border-blue-200 dark:border-blue-800'
                };
            case 'error':
                return {
                    icon: <AlertCircle size={16} />,
                    text: 'Save Failed',
                    color: 'text-red-600',
                    bg: 'bg-red-50 dark:bg-red-900/20',
                    border: 'border-red-200 dark:border-red-800'
                };
            case 'offline':
                return {
                    icon: <CloudOff size={16} />,
                    text: 'Offline',
                    color: 'text-gray-600',
                    bg: 'bg-gray-50 dark:bg-gray-900/20',
                    border: 'border-gray-200 dark:border-gray-800'
                };
        }
    };

    const { icon, text, color, bg, border } = getStatusDisplay();

    const formatTime = (date: Date) => {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (seconds < 10) return 'just now';
        if (seconds < 60) return `${seconds}s ago`;
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${bg} ${color} ${border} transition-all`}>
            {icon}
            <div className="flex flex-col">
                <span className="text-sm font-medium leading-none">{text}</span>
                {lastSyncTime && status === 'synced' && (
                    <span className="text-xs opacity-70 leading-none mt-0.5">
                        {formatTime(lastSyncTime)}
                    </span>
                )}
            </div>

            {/* Source of Truth Indicator */}
            <div className="ml-2 pl-2 border-l border-current/20 flex items-center gap-1">
                <Database size={12} className="opacity-70" />
                <span className="text-xs opacity-70 font-medium">
                    {sourceOfTruth === 'backend' ? 'Backend' : 'Cache'}
                </span>
            </div>
        </div>
    );
};

export default SyncStatusIndicator;
